// ─────────────────────────────────────────────
// constants.js
// Shared constants for LoadDash / AtlasRouteEQ
// License: MIT (your original IP)
// ─────────────────────────────────────────────

// Brand
export const BRAND_NAME = "LoadDash";

// Brand colors
export const NAVY  = "#0a1628";
export const NAVY2 = "#112240";
export const BLUE  = "#1565c0";
export const BLUE2 = "#1976d2";
export const STEEL = "#607d9f";
export const MUTED = "#8fa5be";
export const BORDER= "#dce4ef";
export const TEXT2 = "#4a6080";
export const GREEN = "#00897b";
export const AMBER = "#f59e0b";
export const RED   = "#e53935";
export const PURPLE= "#7c3aed";
export const BG    = "#f4f6f9";

// Shared card / input styles
export const card = {
  background: "#fff",
  borderRadius: 20,
  border: `1px solid ${BORDER}`,
  padding: 24,
  marginBottom: 10,
  boxShadow: "0 2px 12px rgba(10,22,40,.07)",
};
export const lbl = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: STEEL,
  display: "block",
  marginBottom: 8,
};
export const inp = {
  width: "100%",
  background: "#f6f9fc",
  border: `1.5px solid ${BORDER}`,
  borderRadius: 8,
  color: NAVY,
  fontSize: 14,
  padding: "11px 13px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};
export const btn = (active) => ({
  flex: 1,
  padding: "15px 20px",
  borderRadius: 13,
  border: "none",
  background: active
    ? "linear-gradient(135deg,#2d5bbf 0%,#4a8fe8 100%)"
    : "#b0bec5",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.5,
  cursor: active ? "pointer" : "not-allowed",
  boxShadow: active ? "0 4px 16px rgba(59,111,212,0.3)" : "none",
});

// Load tiers
export const LOAD_TIERS = [
  { key: "Small",  label: "Small Load",  cap: "1 to 5 items",    price: 75,  items: "A few trash bags, boxes, or loose household items.",                                         fill: 1,   bags: "1–5",   img: "/Load-Visuals/small.png", popular: true },
  { key: "Half",   label: "Medium Load", cap: "6 to 15 items",   price: 120, items: "Several bags, boxes, or loose items. Good for small cleanouts.",                            fill: 2,   bags: "6–15",  img: "/Load-Visuals/half.png" },
  { key: "ThreeQ", label: "Large Load",  cap: "16 to 30 items",  price: 175, items: "Large amount of bags, boxes, or yard debris. (No large furniture or appliances)",          fill: 3,   bags: "16–30", img: "/Load-Visuals/threequarter.png" },
  { key: "Full",   label: "PCS Cleanout", cap: "Truck bed full", price: 295, items: "Large cleanouts with many bags, boxes, loose items, and lightweight furniture.",           fill: 6,   bags: "30+",   img: "/Load-Visuals/full.png" },
];

// Booking steps
export const STEPS = [
  { num: 1, title: "Upload Photos",   sub: "Photos of items for removal" },
  { num: 2, title: "Item Type",       sub: "What are you disposing?" },
  { num: 3, title: "Pickup Location", sub: "Address and driver notes" },
  { num: 4, title: "Load Size",       sub: "Review Your Load Size" },
  { num: 5, title: "Pickup Window",   sub: "Choose a date and time" },
  { num: 6, title: "Confirm & Pay",   sub: "Review and pay to confirm" },
];

// Payment methods
export const PAYMENT_METHODS = [
  { key: "ApplePay",  color: "#000000", bg: "#f5f5f5", border: "#d1d5db", handle: "",  note: "Apple Pay, Google Pay, or saved wallet — instant confirmation" },
  { key: "SavedCard", color: "#635bff", bg: "#f0f0ff", border: "#a5b4fc", handle: "",  note: "Use a saved card on file" },
  { key: "Card",      color: "#635bff", bg: "#f0f0ff", border: "#a5b4fc", handle: "",  note: "Credit or debit card — instant confirmation" },
  { key: "Cash App",  color: "#00c244", bg: "#e8fff0", border: "#86efac", handle: process.env.REACT_APP_CASHAPP_HANDLE || "$LoadDashServices", note: "Send via Cash App" },
];

// Pickup slots
export const PICKUP_SLOTS = [
  { day: "Tuesday",  time: "10:00 AM – 1:00 PM" },
  { day: "Saturday", time: "12:00 PM – 3:00 PM" },
];

// Max jobs per zone per day (legacy — kept for backward compatibility)
export const ZONE_CAP = 8;

// LEU capacity — single source of truth
// LOAD_TIERS[].fill is the LEU value per tier (Small=1, Half=2, ThreeQ=3, Full=6)
// Item type LEU reference: Cardboard = 15 flat broken-down boxes per 1 LEU
export const TRUCK_MAX_LEU = 18;    // Route planning scale (not physical bed capacity)
export const TRUCK_RUN_CAPACITY_LEU = 3; // Physical truck bed capacity per run
export const ZONE_MAX_LEU  = 24;    // Max LEU per zone per day (8 jobs × avg 3 LEU)
export const DAY_MAX_LEU   = 48;    // Max LEU across all zones per day

/** Get LEU for a load size key. Single derivation point from LOAD_TIERS.fill */
export function getLEU(sizeKey) {
  return LOAD_TIERS.find(t => t.key === sizeKey)?.fill || 1;
}

// ── Model A Earnings Split Constants ──────────────────────
export const COMPANY_MARGIN_PCT = 0.10;   // 10% company margin on gross
export const STRIPE_FEE_PCT    = 0.035;   // 3.5% Stripe processing fee
// DUMP_FEE and FUEL_RATE_PER_MILE removed — use MARKETS[marketId] in
// src/utils/marketConfig.js as the single source of truth for cost inputs.

// Zone definitions
export const ZONES = [
  {
    key: "A", label: "Zone A — North", color: "#1565c0", bg: "#e3f2fd", avgMileage: 4.2,
    streets: "Grant Cir, Lincoln Cir, Washington St, Truman Cir, N Liberty Way (upper), N Church St (upper), Appomattox Cir",
    keywords: ["grant cir","grant circle","lincoln cir","lincoln circle","washington st","washington street","truman cir","truman circle","appomattox","n church st","n church street","n liberty wy","n liberty way"],
  },
  {
    key: "B", label: "Zone B — Central", color: "#00897b", bg: "#e0f2f1", avgMileage: 3.1,
    streets: "Charlestown Loop, Bedford Cir, Braddock Cir, N Church St (mid), N Liberty Way (mid), Gettysburg Cir, Shenandoah Cir, Bull Run Cir, Trenton Cir",
    keywords: ["charlestown loop","charleston loop","charlestown lp","charlestown","bedford cir","bedford circle","braddock","n church","gettysburg","shenandoah","bull run","trenton cir","trenton circle"],
  },
  {
    key: "C", label: "Zone C — Southwest", color: "#6d28d9", bg: "#ede9fe", avgMileage: 5.8,
    streets: "Airlane Dr, Harrington Loop, Cambridge Loop, Roxbury Cir, Princeton Dr, Hamilton Dr, Adams Cir, Hancock Cir, E 200 S, E 225 S, Arsenal Rd, 6th St",
    keywords: ["airlane dr","airlane drive","s airlane","harrington","cambridge lp","cambridge loop","roxbury","princeton dr","princeton drive","princeton st","princeton","hamilton dr","hamilton drive","hamilton st","hamilton","adams cir","adams circle","hancock","e 200 s","e 225 s","arsenal","6th st","6th street"],
  },
  {
    key: "D", label: "Zone D — Southeast", color: "#c2410c", bg: "#fff7ed", avgMileage: 6.4,
    streets: "Colony Loop, Yorktown Way, Freedom Cir, Freedom Dr, Lexington Way, Concord Way, Congress Way, Independence Ln, Minuteman Way, Constitution Way, Franklin Way, Hale Way, Liberty Way, Saratoga Way, Revere Cir, E 700 S",
    keywords: ["colony loop","colony lp","colony cir","colony circle","colony","yorktown wy","yorktown way","yorktown st","yorktown","freedom cir","freedom circle","freedom dr","freedom drive","freedom","lexington wy","lexington way","lexington st","lexington","concord wy","concord way","concord st","concord","congress wy","congress way","congress st","congress","independence ln","independence lane","independence st","independence","minuteman wy","minuteman way","minuteman st","minuteman","constitution wy","constitution way","constitution rd","constitution","franklin wy","franklin way","franklin st","franklin","hale wy","hale way","hale","liberty wy","liberty way","liberty st","liberty","saratoga wy","saratoga way","saratoga st","saratoga","revere cir","revere circle","revere","e 700 s"],
  },
];

// Zone polygons for coordinate-based detection
export const ZONE_POLYGONS = {
  A: [
    [41.12951,-112.00333],[41.12819,-112.00163],[41.12521,-112.00114],
    [41.12173,-112.00235],[41.11975,-112.00138],[41.11793,-111.99822],
    [41.11500,-111.99700],[41.11500,-111.97500],[41.11500,-111.96000],
    [41.11809,-111.95886],[41.11975,-111.96080],[41.12173,-111.96323],
    [41.12372,-111.96566],[41.12554,-111.96809],[41.12703,-111.97198],
    [41.12852,-111.97684],[41.12951,-111.98170],[41.13017,-111.98753],
    [41.13050,-111.99385],[41.13001,-111.99968],[41.12951,-112.00163],
  ],
  D: [
    [41.11500,-111.99700],[41.11412,-111.99725],[41.11147,-111.99482],
    [41.10983,-111.99288],[41.10783,-111.99045],[41.10518,-111.98850],
    [41.10253,-111.98777],[41.10088,-111.98559],[41.09989,-111.98267],
    [41.09939,-111.97976],[41.09922,-111.97684],[41.09939,-111.97295],
    [41.09989,-111.96906],[41.10055,-111.96518],[41.10138,-111.96129],
    [41.10220,-111.95691],[41.10320,-111.95351],[41.10452,-111.95157],
    [41.10651,-111.95060],[41.10849,-111.95108],[41.11048,-111.95254],
    [41.11246,-111.95448],[41.11445,-111.95546],[41.11644,-111.95691],
    [41.11500,-111.96000],[41.11500,-111.97500],
  ],
};

// Authorized admin emails
export const ADMIN_EMAILS = [
  "diohv@loaddashservices.com",
];

// Admin phone for misrep alerts (set REACT_APP_ADMIN_PHONE in .env.local)
export const ADMIN_PHONE = process.env.REACT_APP_ADMIN_PHONE || "";

// Mapbox token (from env)
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export const BRAND_COLORS = {
  NAVY:   '#0A1628',
  NAVY2:  '#012340',
  BLUE:   '#3D90D9',
  GRAY:   '#F2F2F2',
  ORANGE: '#F29727',
  WHITE:  '#FFFFFF',
};
